import { supabase } from '@/lib/supabase';
import { PremiumBot } from '@/types/premium-bots';
import { v4 as uuidv4 } from 'uuid';
import { createShadowBot, deleteShadowBot } from '../shadow-bot-service';

/**
 * Fetch all premium bots with pagination, search, and filtering
 */
export const fetchAdminPremiumBots = async ({
  page = 1,
  pageSize = 10,
  searchQuery = '',
  sortBy = 'created_date',
  sortOrder = 'desc',
  status = 'all',
}: {
  page?: number;
  pageSize?: number;
  searchQuery?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: string;
}) => {
  try {
    // Calculate pagination
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    // Start building the query
    let query = supabase
      .from('bots')
      .select(`
        id,
        name,
        description,
        status,
        color_scheme,
        type,
        created_date,
        is_featured,
        is_new,
        is_best_seller,
        premiumDetails:bot_premium_details(*)
      `, { count: 'exact' })
      .eq('type', 'premium_bot');

    console.log('Building query for premium bots with params:', { page, pageSize, searchQuery, sortBy, sortOrder, status });

    // Add search if provided
    if (searchQuery) {
      query = query.or(`name.ilike.%${searchQuery}%, description.ilike.%${searchQuery}%`);
    }

    // Add status filter if provided and not 'all'
    if (status && status !== 'all') {
      query = query.eq('status', status);
    }

    // Add sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Add pagination
    query = query.range(start, end);

    // Execute query
    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching admin premium bots:', error);
      throw error;
    }

    console.log('Fetched premium bots:', data);
    
    // Cast to PremiumBot type with proper type assertion
    const typedPremiumBots = data as unknown as PremiumBot[];

    return {
      bots: typedPremiumBots,
      totalCount: count || 0,
      currentPage: page,
      pageSize,
      totalPages: Math.ceil((count || 0) / pageSize),
    };
  } catch (error) {
    console.error('Error fetching admin premium bots:', error);
    throw error;
  }
};

/**
 * Fetch a single premium bot by ID
 */
export const fetchAdminPremiumBotById = async (botId: string) => {
  try {
    const { data: bot, error: botError } = await supabase
      .from('bots')
      .select('*')
      .eq('id', botId)
      .eq('type', 'premium_bot')
      .single();

    if (botError) throw botError;

    // Get premium details
    const { data: premiumDetails, error: detailsError } = await supabase
      .from('bot_premium_details')
      .select('*')
      .eq('bot_id', botId)
      .single();

    if (detailsError && !detailsError.message.includes('No rows found')) {
      throw detailsError;
    }

    // Log the data to debug
    console.log('Fetched premium bot by ID:', bot);
    console.log('Fetched premium details:', premiumDetails);

    // For consistency, ensure the premiumDetails is properly formatted
    const formattedDetails = premiumDetails ? {
      ...premiumDetails,
      // Ensure array fields are properly parsed from string if needed
      pairs: Array.isArray(premiumDetails.pairs) ? premiumDetails.pairs : 
             (typeof premiumDetails.pairs === 'string' && premiumDetails.pairs ? 
              JSON.parse(premiumDetails.pairs) : []),
      markets: Array.isArray(premiumDetails.markets) ? premiumDetails.markets : 
              (typeof premiumDetails.markets === 'string' && premiumDetails.markets ? 
               JSON.parse(premiumDetails.markets) : []),
      features: Array.isArray(premiumDetails.features) ? premiumDetails.features : 
               (typeof premiumDetails.features === 'string' && premiumDetails.features ? 
                JSON.parse(premiumDetails.features) : []),
    } : null;

    return {
      ...bot,
      premiumDetails: formattedDetails,
    } as PremiumBot;
  } catch (error) {
    console.error('Error fetching premium bot by ID:', error);
    throw error;
  }
};

/**
 * Delete a premium bot
 */
export const deletePremiumBot = async (botId: string) => {
  try {
    // First delete shadow bot if it exists
    await deleteShadowBot(botId);
    
    // First delete any premium details
    const { error: detailsError } = await supabase
      .from('bot_premium_details')
      .delete()
      .eq('bot_id', botId);
    
    // Ignore not found errors for details
    if (detailsError && !detailsError.message.includes('No rows found')) {
      throw detailsError;
    }
    
    // Then delete the bot
    const { error: botError } = await supabase
      .from('bots')
      .delete()
      .eq('id', botId)
      .eq('type', 'premium_bot');
      
    if (botError) throw botError;
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting premium bot:', error);
    throw error;
  }
};

/**
 * Create a new premium bot
 */
export const createPremiumBot = async (botData: Partial<PremiumBot>) => {
  try {
    // Extract premium details from the bot data
    const { premiumDetails, ...botInfo } = botData;
    
    // Make sure we set the type to premium_bot and generate required tokens
    const botPayload = {
      ...botInfo,
      type: 'premium_bot',
      signal_token: uuidv4(),
      reverse_signal_token: uuidv4(),
      webhook_url: 'https://api.tradebot365.com/api/webhook/order',
      created_date: new Date().toISOString(),
    };
    
    // Insert into bots table
    const { data: newBot, error: botError } = await supabase
      .from('bots')
      .insert([botPayload])
      .select()
      .single();
      
    if (botError) throw botError;
    
    // Create shadow bot after successful creation
    try {
      await createShadowBot(newBot);
    } catch (shadowError) {
      console.error('Error creating shadow bot for premium bot:', shadowError);
      // Don't fail the main bot creation if shadow bot creation fails
    }
    
    // If we have premium details, add them
    if (premiumDetails) {
      const premiumDetailsPayload = {
        ...premiumDetails,
        bot_id: newBot.id,
      };
      
      const { data: newPremiumDetails, error: premiumDetailsError } = await supabase
        .from('bot_premium_details')
        .insert([premiumDetailsPayload])
        .select()
        .single();
        
      if (premiumDetailsError) throw premiumDetailsError;
      
      return {
        ...newBot,
        premiumDetails: newPremiumDetails,
      } as PremiumBot;
    }
    
    return newBot as PremiumBot;
  } catch (error) {
    console.error('Error creating premium bot:', error);
    throw error;
  }
};

/**
 * Filter out generated columns from premium details
 */
const filterGeneratedColumns = (premiumDetails: any) => {
  const { subscribers_total, connected_accounts_total, ...filteredDetails } = premiumDetails || {};
  return filteredDetails;
};

/**
 * Update an existing premium bot
 */
export const updatePremiumBot = async (botId: string, botData: Partial<PremiumBot>) => {
  try {
    // Extract premium details from the bot data
    const { premiumDetails, ...botInfo } = botData;
    
    // Remove tokens from botInfo to prevent accidentally overwriting them
    const { signal_token, reverse_signal_token, ...safeUpdateData } = botInfo;
    
    // Update bots table (excluding tokens to preserve existing ones)
    const { data: updatedBot, error: botError } = await supabase
      .from('bots')
      .update(safeUpdateData)
      .eq('id', botId)
      .eq('type', 'premium_bot')
      .select()
      .single();
      
    if (botError) throw botError;
    
    // If we have premium details, update them (excluding generated columns)
    if (premiumDetails) {
      // Filter out generated columns before updating
      const filteredPremiumDetails = filterGeneratedColumns(premiumDetails);
      
      console.log('Updating premium details (filtered):', filteredPremiumDetails);
      
      // Check if premium details already exist
      const { data: existingDetails } = await supabase
        .from('bot_premium_details')
        .select()
        .eq('bot_id', botId)
        .single();
      
      let updatedPremiumDetails;
      
      if (existingDetails) {
        // Update existing details
        const { data: premiumDetailsData, error: premiumDetailsError } = await supabase
          .from('bot_premium_details')
          .update(filteredPremiumDetails)
          .eq('bot_id', botId)
          .select()
          .single();
          
        if (premiumDetailsError) throw premiumDetailsError;
        updatedPremiumDetails = premiumDetailsData;
      } else {
        // Insert new details
        const premiumDetailsPayload = {
          ...filteredPremiumDetails,
          bot_id: botId,
        };
        
        const { data: premiumDetailsData, error: premiumDetailsError } = await supabase
          .from('bot_premium_details')
          .insert([premiumDetailsPayload])
          .select()
          .single();
          
        if (premiumDetailsError) throw premiumDetailsError;
        updatedPremiumDetails = premiumDetailsData;
      }
      
      return {
        ...updatedBot,
        premiumDetails: updatedPremiumDetails,
      } as PremiumBot;
    }
    
    return updatedBot as PremiumBot;
  } catch (error) {
    console.error('Error updating premium bot:', error);
    throw error;
  }
};
